'use client';

import { flip, offset, shift } from '@floating-ui/react';
import {
  FloatingLinkUrlInput,
  submitFloatingLink,
  triggerFloatingLinkEdit,
  useFloatingLinkEdit,
  useFloatingLinkEditState,
  useFloatingLinkInsert,
  useFloatingLinkInsertState,
} from '@platejs/link/react';
import { LinkPlugin } from '@platejs/link/react';
import {
  Edit05,
  InfoCircle,
  Link01,
  LinkBroken01,
  LinkExternal01,
} from '@untitledui/icons';
import { useEditorPlugin, useEditorRef } from 'platejs/react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { useStackingZIndex } from '@/contexts/StackingContext';

// ── Hoisted constants ────────────────────────────────────────────────────────

const OFFSCREEN_FALLBACK_RECT: DOMRect = {
  bottom: 9999,
  height: 0,
  left: -9999,
  right: 9999,
  top: -9999,
  width: 0,
  x: 0,
  y: 0,
  toJSON: () => ({}),
};

/**
 * Anchor rect for Plate's floating link popup.
 *
 * Plate's default (`getDOMSelectionBoundingClientRect`) reads
 * `window.getSelection().getRangeAt(0).getBoundingClientRect()`. A collapsed
 * caret on an empty block (e.g. a blank line between paragraphs) returns a
 * zero-rect in Chromium, which makes `shift()` pin the popup to the viewport's
 * top-left. Fall back to the caret's nearest Element ancestor rect in that
 * case so the popup lands near the actual caret line.
 */
const getLinkAnchorRect = (): DOMRect => {
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount < 1) {
    return OFFSCREEN_FALLBACK_RECT;
  }
  const range = domSelection.getRangeAt(0);
  const rect = range.getBoundingClientRect();
  if (rect.width || rect.height || rect.x || rect.y) {
    return rect;
  }
  let node: Node | null = range.startContainer;
  while (node && node.nodeType !== Node.ELEMENT_NODE) {
    node = node.parentNode;
  }
  if (node instanceof Element) {
    return node.getBoundingClientRect();
  }
  return OFFSCREEN_FALLBACK_RECT;
};

const FLOATING_OPTIONS = {
  placement: 'bottom-start' as const,
  middleware: [
    offset({ mainAxis: 8, crossAxis: 0 }),
    flip({ fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] }),
    shift({ padding: 8 }),
  ],
};

// Insert-specific options: override Plate's default anchor rect so a caret on
// an empty line doesn't pin the popup to the viewport's top-left. The edit
// popup keeps Plate's default because that path already derives its rect from
// the link node's extent.
const INSERT_FLOATING_OPTIONS = {
  ...FLOATING_OPTIONS,
  getBoundingClientRect: getLinkAnchorRect,
};

const SLATE_EDITOR_SELECTOR = '[data-slate-editor="true"]';

/**
 * Generous URL heuristic for pre-populating the link input.
 * Matches text starting with http(s):// OR containing a dot followed by a
 * common TLD. Rejects text with whitespace.
 *
 * This is a detection heuristic, NOT a sanitizer — transformInput() in
 * email-editor-plugins.ts handles protocol normalization and sanitization
 * at submission time.
 */
const looksLikeUrl = (text: string): boolean => {
  if (!text || /\s/.test(text)) {
    return false;
  }
  if (/^https?:\/\//i.test(text)) {
    return true;
  }
  return /\.(com|org|net|io|dev|co|ai|app|edu|gov|me|info|xyz|us|uk|ca)\b/i.test(
    text
  );
};

// ── Component ────────────────────────────────────────────────────────────────

export function LinkFloatingToolbar() {
  const editor = useEditorRef();
  // Portaled popup must render above any containing overlay (e.g., proposal
  // slideout at zIndex 9999). This hook reads the nearest StackingLevelProvider
  // via React Context — Context reaches across createPortal.
  // Callers that host this editor in a new overlay MUST wrap in StackingLevelProvider.
  const popupZIndex = useStackingZIndex(1);
  const { getOptions, setOption } = useEditorPlugin(LinkPlugin);

  const insertState = useFloatingLinkInsertState({
    floatingOptions: INSERT_FLOATING_OPTIONS,
  });
  const { props: insertProps, ref: insertRef } =
    useFloatingLinkInsert(insertState);

  const editState = useFloatingLinkEditState({
    floatingOptions: FLOATING_OPTIONS,
  });
  const {
    props: editProps,
    ref: editRef,
    unlinkButtonProps,
  } = useFloatingLinkEdit(editState);

  // Set updated flag when edit mode opens so FloatingLinkUrlInput populates with URL
  React.useEffect(() => {
    if (editState.isOpen) {
      triggerFloatingLinkEdit(editor);
      setOption('updated', true);
      editState.floating.update();
    } else if (insertState.isOpen) {
      const selectedText = getOptions().text?.trim() ?? '';
      // Always reset url here — Plate's link plugin retains the `url` option
      // across opens, so without clearing on non-URL selections the input
      // would prefill with the previous insert's value.
      setOption('url', looksLikeUrl(selectedText) ? selectedText : '');
    } else {
      setOption('updated', false);
    }
  }, [
    editState.isOpen,
    editState.floating,
    insertState.isOpen,
    setOption,
    editor,
    getOptions,
  ]);

  // Track if user is actively selecting text
  const [hasActiveSelection, setHasActiveSelection] = React.useState(false);

  // Track if toolbar was explicitly triggered by user action (click or keyboard shortcut)
  const [wasExplicitlyTriggered, setWasExplicitlyTriggered] =
    React.useState(false);

  // Monitor selection changes to detect active text selection
  React.useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        setHasActiveSelection(false);
        return;
      }
      const range = selection.getRangeAt(0);
      setHasActiveSelection(!range.collapsed);
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  // Listen for mousedown on links to detect explicit click intent
  React.useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const linkElement = target.closest('a');
      if (linkElement) {
        const editorElement = target.closest(SLATE_EDITOR_SELECTOR);
        if (editorElement) {
          setWasExplicitlyTriggered(true);
        }
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  // Listen for Cmd+K / Ctrl+K keyboard shortcut to explicitly trigger toolbar
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        const target = event.target as HTMLElement;
        const editorElement = target.closest(SLATE_EDITOR_SELECTOR);
        if (editorElement) {
          setWasExplicitlyTriggered(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset explicit trigger state when toolbar closes
  React.useEffect(() => {
    if (!editState.isOpen) {
      setWasExplicitlyTriggered(false);
    }
  }, [editState.isOpen]);

  // Show edit toolbar only if NOT actively selecting text AND explicitly triggered
  const shouldShowEdit =
    editState.isOpen && !hasActiveSelection && wasExplicitlyTriggered;

  // ── Stable callbacks ─────────────────────────────────────────────────────

  const handleGoToLink = React.useCallback(() => {
    const url = getOptions().url || '';
    const fullUrl = /^(https?|mailto|tel|slack|ftp):/i.test(url)
      ? url
      : `https://${url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  }, [getOptions]);

  const handleSubmitLink = React.useCallback(() => {
    submitFloatingLink(editor);
  }, [editor]);

  // ── Early return after all hooks ─────────────────────────────────────────

  if (!insertState.isOpen && !shouldShowEdit) {
    return null;
  }

  // Get current URL value for validation feedback
  const currentUrl = getOptions().url || '';

  // Check if URL will be auto-formatted (doesn't have protocol)
  const willAutoFormat =
    currentUrl &&
    !/^(https?|mailto|tel):\/\//i.test(currentUrl) &&
    !currentUrl.startsWith('/') &&
    !currentUrl.startsWith('#');

  const input = (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center gap-2'>
        <FloatingLinkUrlInput
          className='flex-1 px-3 py-2 text-sm border border-primary rounded-md outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2'
          placeholder='Enter URL (e.g., example.com or https://example.com)'
        />
      </div>
      {willAutoFormat && currentUrl && (
        <div className='flex items-center gap-1 px-2 text-xs text-tertiary'>
          <InfoCircle className='size-3' />
          <span>Will be saved as: https://{currentUrl}</span>
        </div>
      )}
    </div>
  );

  // Destructure style from props to handle React 19 type compatibility
  const { style: editStyle, ...editPropsRest } = editProps;
  const { style: insertStyle, ...insertPropsRest } = insertProps;

  const editContent = shouldShowEdit ? (
    <div
      ref={editRef}
      {...editPropsRest}
      style={{ ...editStyle, zIndex: popupZIndex } as React.CSSProperties}
      className='w-auto min-w-[340px] rounded-lg border border-secondary bg-primary shadow-lg'
      // React Aria's AriaModalOverlay (used by ProposalSidebar) calls
      // ariaHideOutside({ shouldUseInert: true }), which sets inert=true on
      // every new child of document.body that isn't inside the modal. This
      // attribute is the documented escape hatch — ariaHideOutside keeps
      // elements tagged `data-react-aria-top-layer="true"` interactive. It's
      // the same mechanism React Aria's own Popover/Menu/Tooltip use to stay
      // interactive inside modals. Without it, the popup renders but cannot
      // receive focus, typing, or clicks.
      data-react-aria-top-layer='true'
    >
      {/* URL Input Section */}
      <div className='p-3 border-b border-secondary'>
        <div className='flex items-center gap-2 mb-2'>
          <Link01 className='size-4 text-tertiary' />
          <span className='text-caption text-secondary'>Edit link</span>
        </div>
        {input}
      </div>

      {/* Action Buttons */}
      <div className='p-2 flex flex-col gap-1'>
        {currentUrl && (
          <button
            type='button'
            onClick={handleGoToLink}
            className='w-full flex items-center gap-3 px-3 py-2 text-sm text-secondary hover:bg-secondary rounded transition duration-100 ease-linear text-left'
          >
            <LinkExternal01 className='size-4 text-tertiary' />
            <span>Go to link</span>
          </button>
        )}

        <button
          type='button'
          onClick={handleSubmitLink}
          className='w-full flex items-center gap-3 px-3 py-2 text-sm text-secondary hover:bg-secondary rounded transition duration-100 ease-linear text-left'
        >
          <Edit05 className='size-4 text-tertiary' />
          <span>Change</span>
        </button>

        <button
          type='button'
          {...unlinkButtonProps}
          className='w-full flex items-center gap-3 px-3 py-2 text-sm text-error-primary hover:bg-error-primary rounded transition duration-100 ease-linear text-left'
        >
          <LinkBroken01 className='size-4 text-fg-error-secondary' />
          <span>Remove</span>
        </button>
      </div>
    </div>
  ) : null;

  const insertContent = insertState.isOpen ? (
    <div
      ref={insertRef}
      {...insertPropsRest}
      style={{ ...insertStyle, zIndex: popupZIndex } as React.CSSProperties}
      className='w-auto min-w-[340px] rounded-lg border border-secondary bg-primary shadow-lg'
      // See comment on editContent's data-react-aria-top-layer above.
      data-react-aria-top-layer='true'
    >
      {/* URL Input Section — mirrors editContent's header so both popups feel
          like the same surface. */}
      <div className='p-3 border-b border-secondary'>
        <div className='flex items-center gap-2 mb-2'>
          <Link01 className='size-4 text-tertiary' />
          <span className='text-caption text-secondary'>Insert link</span>
        </div>
        {input}
      </div>

      {/* Action Button */}
      <div className='p-2'>
        <button
          type='button'
          onClick={handleSubmitLink}
          className='w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-brand-solid hover:bg-brand-solid_hover rounded transition duration-100 ease-linear'
        >
          <Link01 className='size-4' />
          <span>Insert link</span>
        </button>
      </div>
    </div>
  ) : null;

  return createPortal(
    <>
      {editContent}
      {insertContent}
    </>,
    document.body
  );
}
